class ThemeDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-mobile-drawer]');
    this.openButton = document.querySelector('[data-menu-open]');
    this.closeButtons = document.querySelectorAll('[data-menu-close]');
    this.panel = this.drawer?.querySelector('.mobile-drawer__panel');
    if (!this.drawer || !this.openButton) return;
    this.openButton.addEventListener('click', () => this.open());
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.openButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add('menu-open');
    window.requestAnimationFrame(() => this.panel?.focus());
  }

  close() {
    if (!this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.openButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
    this.drawer.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    this.openButton.focus({ preventScroll: true });
  }
}

class EditorialHeader {
  constructor() {
    this.header = document.querySelector('[data-site-header]');
    if (!this.header) return;
    this.lastScroll = window.scrollY;
    this.update = this.update.bind(this);
    window.addEventListener('scroll', this.update, { passive: true });
    this.update();
  }

  update() {
    const current = window.scrollY;
    this.header.classList.toggle('is-scrolled', current > 36);
    this.header.classList.toggle('is-hidden', current > this.lastScroll && current > 360);
    if (current < this.lastScroll - 12 || current < 120) this.header.classList.remove('is-hidden');
    this.lastScroll = current;
  }
}

class RevealController {
  constructor(root = document) {
    this.elements = [...root.querySelectorAll('[data-reveal]')];
    if (!this.elements.length) return;

    this.elements.forEach((element) => {
      const delay = Number(element.dataset.revealDelay || 0);
      element.style.setProperty('--reveal-delay', `${Math.min(delay, 600)}ms`);
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      this.elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        this.observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    this.elements.forEach((element) => this.observer.observe(element));
  }
}

function respectReducedMotion() {
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.querySelectorAll('video[autoplay]').forEach((video) => {
    video.pause();
    video.removeAttribute('autoplay');
  });
}

function initScrollCues(root = document) {
  root.querySelectorAll('[data-scroll-next]').forEach((button) => {
    if (button.dataset.scrollReady === 'true') return;
    button.dataset.scrollReady = 'true';
    button.addEventListener('click', () => {
      const hero = button.closest('section');
      const next = hero?.parentElement?.nextElementSibling || hero?.nextElementSibling;
      next?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
  });
}

function initTheme(root = document) {
  new RevealController(root);
  initScrollCues(root);
  respectReducedMotion();
}

document.addEventListener('DOMContentLoaded', () => {
  new ThemeDrawer();
  new EditorialHeader();
  initTheme(document);
});

document.addEventListener('shopify:section:load', (event) => initTheme(event.target));

class PrivateReserveHero {
  constructor(root = document) {
    this.hero = root.querySelector?.('[data-v3-hero]');
    if (!this.hero || this.hero.dataset.v3Ready === 'true') return;
    this.hero.dataset.v3Ready = 'true';
    this.video = this.hero.querySelector('video');
    this.toggle = this.hero.querySelector('[data-v3-motion-toggle]');
    this.label = this.hero.querySelector('[data-v3-motion-label]');

    if (this.video && this.toggle) {
      this.toggle.addEventListener('click', () => this.toggleMotion());
    }

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && window.matchMedia('(pointer:fine)').matches) {
      this.hero.addEventListener('pointermove', (event) => this.handlePointer(event), { passive: true });
      this.hero.addEventListener('pointerleave', () => this.resetPointer(), { passive: true });
    }
  }

  toggleMotion() {
    if (!this.video) return;
    if (this.video.paused) {
      this.video.play().catch(() => {});
      this.toggle.setAttribute('aria-pressed', 'false');
      this.toggle.setAttribute('aria-label', 'Pause background video');
      if (this.label) this.label.textContent = 'Pause motion';
    } else {
      this.video.pause();
      this.toggle.setAttribute('aria-pressed', 'true');
      this.toggle.setAttribute('aria-label', 'Play background video');
      if (this.label) this.label.textContent = 'Play motion';
    }
  }

  handlePointer(event) {
    const rect = this.hero.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    this.hero.style.setProperty('--v3-pointer-x', `${x * 5}px`);
    this.hero.style.setProperty('--v3-pointer-y', `${y * 3}px`);
  }

  resetPointer() {
    this.hero.style.setProperty('--v3-pointer-x', '0px');
    this.hero.style.setProperty('--v3-pointer-y', '0px');
  }
}

document.addEventListener('DOMContentLoaded', () => new PrivateReserveHero(document));
document.addEventListener('shopify:section:load', (event) => new PrivateReserveHero(event.target));


class V4Scroller {
  constructor(root = document) {
    root.querySelectorAll?.('[data-v4-scroller-wrap]').forEach((wrap) => {
      if (wrap.dataset.v4Ready === 'true') return;
      wrap.dataset.v4Ready = 'true';
      const scroller = wrap.querySelector('[data-v4-scroller]');
      if (!scroller) return;
      wrap.querySelectorAll('[data-v4-scroll]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.v4Scroll === 'next' ? 1 : -1;
          const card = scroller.querySelector(':scope > *');
          const amount = card ? card.getBoundingClientRect().width + 20 : scroller.clientWidth * .8;
          scroller.scrollBy({ left: direction * amount, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        });
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new V4Scroller(document));
document.addEventListener('shopify:section:load', (event) => new V4Scroller(event.target));

function getV44BundledRoutes() {
  if (window.__v44BundledRoutes) return window.__v44BundledRoutes;
  const routeData = document.getElementById('V4BundledRoutes');
  if (!routeData) return {};
  try {
    window.__v44BundledRoutes = JSON.parse(routeData.textContent || '{}');
  } catch (error) {
    window.__v44BundledRoutes = {};
  }
  return window.__v44BundledRoutes;
}

function applyV44BundledRoutes(root = document) {
  const routes = getV44BundledRoutes();
  if (!Object.keys(routes).length) return;

  root.querySelectorAll?.('a[href]').forEach((anchor) => {
    const rawHref = anchor.getAttribute('href');
    if (!rawHref || rawHref.startsWith('#') || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) return;

    let parsed;
    try {
      parsed = new URL(rawHref, window.location.origin);
    } catch (error) {
      return;
    }

    if (parsed.origin !== window.location.origin) return;
    const normalizedPath = parsed.pathname.length > 1 ? parsed.pathname.replace(/\/$/, '') : parsed.pathname;
    const resolved = routes[normalizedPath];
    if (!resolved) return;

    anchor.setAttribute('href', `${resolved}${parsed.hash || ''}`);
  });
}

document.addEventListener('DOMContentLoaded', () => applyV44BundledRoutes(document));
document.addEventListener('shopify:section:load', (event) => applyV44BundledRoutes(event.target));
